import { defineStore } from "pinia";
import { BookingsApi, type BookingNotificationsSettingsDto } from "@simbelapp/order-sdk";
import { useSessionsStore } from "../sessions/sessions-store";
import { useTrainingsStore } from "../trainings/trainings.store";
import { useFeedback } from "~/composables/feedback/useFeedback";
import type { IBookingsStore, IUpdateBookingSessionPayload } from "~/utils/interfaces/bookings-interfaces";
import { useEmployeeTrainingsStore } from "~/store/employee-trainings/employee-trainings.store";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useFormats } from "~/composables/utils/useFormat";

export const useBookingsStore = defineStore("bookingsStore", {
  state: (): IBookingsStore => {
    return {
      tmpBookingLegalEntity: null,
      tmpBookingGroups: [],
    };
  },
  getters: {},
  actions: {
    async updateBookingSession(updateBookingSessionPayload: IUpdateBookingSessionPayload): Promise<any> {
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
        await apiInstance.updateBookingSession({ body: updateBookingSessionPayload });
        feedback.success("Vos modifications ont bien été sauvegardées", "small");
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },

    async cancelBooking(bookingId: string, doNotNotify: boolean, requestId?: string | null): Promise<boolean> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
        await apiInstance.cancelBooking({
          id: bookingId,
          cancelBookingDto: { doNotNotify, requestId: requestId ?? null },
        });
        return true;
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return false;
      }
    },

    async confirmBooking(bookingId: string, sessionId: string): Promise<boolean> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
        await apiInstance.confirmBooking({
          id: bookingId,
          confirmBookingDto: { sessionId },
        });
        return true;
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return false;
      }
    },
    async updateBookingNotificationsSettings(
      bookingId: string,
      bookingNotificationsSettingsDto: BookingNotificationsSettingsDto,
    ) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
      await apiInstance.updateBookingNotificationsSettings({
        bookingId,
        bookingNotificationsSettingsDto,
      });
    },

    async updateBookingLegalEntity(bookingId: string, legalEntityId: string): Promise<boolean> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
        await apiInstance.updateBookingLegalEntity({
          bookingId,
          updateBookingLegalEntityDto: { legalEntityId },
        });
        return true;
      } catch {
        feedback.error("Une erreur est survenue lors de la mise à jour de l'entité légale", "small");
        return false;
      }
    },

    async updateBookingUserGroups(bookingId: string, userGroupIds: string[]): Promise<boolean> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
        await apiInstance.updateBookingUserGroups({
          bookingId,
          updateBookingUserGroupsDto: { userGroupIds },
        });
        return true;
      } catch {
        feedback.error("Une erreur est survenue lors de la mise à jour des groupes", "small");
        return false;
      }
    },

    async setEndOfTraining({
      trainingValidated,
      manualDuration,
      noShowDate,
      noShowReason,
      noShowDesc,
    }: {
      trainingValidated?: boolean;
      manualDuration?: number;
      noShowDate?: Date;
      noShowReason?: string | null;
      noShowDesc?: string;
    }): Promise<any> {
      const { trainingPlanSessionIndividual, fetchTrainingPlanSession } = useSessionsStore();
      const trainingStore = useTrainingsStore();
      const feedback = useFeedback();
      const format = useFormats();
      if (trainingPlanSessionIndividual?.trainingId) {
        await trainingStore.fetchOwnPartnerTrainingDetails(trainingPlanSessionIndividual?.trainingId);
      }
      const isDurationComplete =
        trainingPlanSessionIndividual?.attendedHours === trainingPlanSessionIndividual?.trainingDuration;

      if (
        trainingPlanSessionIndividual?.sessionId &&
        trainingPlanSessionIndividual?.bookingId &&
        trainingPlanSessionIndividual?.startDate
      ) {
        const { trainingDetails } = useTrainingsStore();
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
          await apiInstance.setEndOfTraining({
            setEndOfTrainingDto: {
              sessionId: trainingPlanSessionIndividual?.sessionId,
              bookingId: trainingPlanSessionIndividual?.bookingId,
              originId: trainingPlanSessionIndividual?.requestId || null,
              manualDuration:
                manualDuration !== undefined
                  ? manualDuration
                  : !isDurationComplete
                    ? trainingPlanSessionIndividual?.attendedHours
                    : undefined,
              startDate: trainingPlanSessionIndividual?.startDate
                ? format.toDateDisplayNoUTC(trainingPlanSessionIndividual?.startDate)
                : null,
              endDate: trainingPlanSessionIndividual?.endDate
                ? format.toDateDisplayNoUTC(trainingPlanSessionIndividual?.endDate)
                : null,
              noShowDate: noShowDate || undefined,
              noShowReason: noShowReason || undefined,
              noShowDesc: noShowDesc || undefined,
              ...(trainingDetails?.habilitation?.habilitationId && {
                habilitationId: trainingDetails?.habilitation?.habilitationId,
                trainingValidated: trainingValidated,
                durationYears: trainingDetails?.habilitation?.durationYears,
              }),
            },
          });
        } catch {
          feedback.error("Une erreur est survenue", "small");
          return;
        }
        // Write succeeded — a failing refresh must not be reported as a write failure
        feedback.greeting("Vos modifications ont bien été sauvegardées", "small");
        const id = trainingPlanSessionIndividual?.id || "";
        await fetchTrainingPlanSession(id).catch(() => {});
      }
    },

    async editEndOfTraining({
      noShowDate,
      noShowReason,
      noShowDesc,
    }: {
      noShowDate?: Date;
      noShowReason?: string | null;
      noShowDesc?: string;
    }): Promise<any> {
      const { trainingPlanSessionIndividual, fetchTrainingPlanSession } = useSessionsStore();
      const feedback = useFeedback();
      if (trainingPlanSessionIndividual?.bookingId) {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
        const data = await apiInstance.updateNoShow({
          bookingId: trainingPlanSessionIndividual?.bookingId,
          updateNoShowBookingDto: {
            noShowDate: noShowDate,
            noShowReason: noShowReason ?? undefined,
            noShowDesc: noShowDesc === "" ? undefined : noShowDesc,
          },
        });
        if (data) {
          const id = trainingPlanSessionIndividual?.id || "";
          await fetchTrainingPlanSession(id);

          feedback.greeting("Vos modifications ont bien été sauvegardées", "small");
          return data;
        } else {
          feedback.error("Une erreur est survenue", "small");
        }
      }
    },

    async setEndOfTrainingLMS(bookingId: string, sessionId: string, originId?: string | null): Promise<void> {
      const employeeTrainingsStore = useEmployeeTrainingsStore();
      const feedback = useFeedback();

      if (bookingId && sessionId) {
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
          await apiInstance.setEndOfTraining({
            setEndOfTrainingDto: {
              sessionId,
              bookingId,
              endDate: new Date(),
              originId: originId || null,
            },
          });
        } catch {
          feedback.error("Une erreur est survenue", "small");
          return;
        }
        // Write succeeded — a failing refresh must not be reported as a write failure
        await employeeTrainingsStore.fetchEmployeeTraining(bookingId).catch(() => {});
      }
    },
  },
});
